import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, Moon, Brain, Heart } from 'lucide-react';
import { Howl } from 'howler';

interface GuidedExperienceProps {
  onClose: () => void;
}

const experiences = [
  {
    id: 'body-scan',
    name: 'Body Scan',
    description: 'A gentle journey through your body to release tension',
    duration: '10 min',
    icon: <Brain className="w-5 h-5" />,
    color: 'from-primary to-purple-500'
  },
  {
    id: 'loving-kindness',
    name: 'Loving Kindness',
    description: 'Cultivate compassion for yourself and others',
    duration: '15 min',
    icon: <Heart className="w-5 h-5" />,
    color: 'from-purple-500 to-blue-500'
  },
  {
    id: 'sleep',
    name: 'Sleep Journey',
    description: 'Drift into peaceful sleep with gentle guidance',
    duration: '20 min',
    icon: <Moon className="w-5 h-5" />,
    color: 'from-blue-500 to-cyan-500'
  }
];

export function GuidedExperience({ onClose }: GuidedExperienceProps) {
  const [selectedExperience, setSelectedExperience] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto mindful-scrollbar">
      <div className="min-h-screen p-6">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between sticky top-0 bg-background/50 backdrop-blur-sm py-4 z-50">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Volume2 className="w-6 h-6 text-primary" />
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
              </div>
              <h2 className="text-xl font-semibold">Guided Experience</h2>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {experiences.map((exp) => (
                <motion.div
                  key={exp.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setSelectedExperience(exp.id)}
                  className="theme-card p-6 relative overflow-hidden group cursor-pointer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${exp.color} opacity-5 group-hover:opacity-10 transition-opacity`} />
                  <div className="relative space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {exp.icon}
                          <div className="absolute inset-0 bg-current opacity-20 blur-lg rounded-full" />
                        </div>
                        <div>
                          <h3 className="font-medium">{exp.name}</h3>
                          <div className="text-sm text-muted-foreground">{exp.duration}</div>
                        </div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 rounded-lg bg-primary/20 text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsPlaying(!isPlaying);
                        }}
                      >
                        {isPlaying ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5" />
                        )}
                      </motion.button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {exp.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}